import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
  Tab,
  Tabs,
  Chip
} from "@mui/material";
import {
  People,
  ShoppingCart,
  AttachMoney,
  Inventory
} from "@mui/icons-material";
import { Line } from "react-chartjs-2";

import { styled } from "@mui/material/styles";
// 🟢 Chart.js scale registration (must fix canvas error)
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getUsers, getVisitor } from "../../State/User/Action";
import { getVendors } from "../../State/Vendor/Action";
import { getIncome, getOrders, getOrderStats, getRecentOrders } from "../../State/Order/Action";
import { getPayments } from "../../State/Payment/Action";
import { getAllEmployee } from "../../State/Employee/Action";
import { getProducts } from "../../State/Product/Action";
import dayjs from "dayjs";
import ImagePopup from "../../../Common/ImagePopup";
import defaultImage from "../../../Common/productDefaultImage.png"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Custom styled Tabs with #449AEF as indicator color
const CustomTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    backgroundColor: "#449AEF"
  }
});

// Custom styled Tab with #449AEF for selected label
const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 500,
  color: "#6b7280", // default gray-500
  "&.Mui-selected": {
    color: "#449AEF"
  }
}));

const Dashboard = () => {
  const [tabValue, setTabValue] = useState("year");
  const { adminUser, adminVendor, adminOrder, adminProduct } = useSelector(store => store);
  const dispatch = useDispatch();
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    dispatch(getUsers());
    dispatch(getVendors())
    dispatch(getOrders())
    dispatch(getOrderStats())
    dispatch(getRecentOrders())
    dispatch(getAllEmployee())
    dispatch(getPayments());
    dispatch(getProducts());
    dispatch(getIncome());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getVisitor(tabValue));
  }, [dispatch, tabValue]);

  const getStatusChip = (status) => {
    const colorMap = {
      "PENDING": "warning",
      "PLACED": "dark",
      "SHIPPED": "info",
      "DELIVERED": "success",
      "CANCELLED": "error"
    };
    return <Chip label={status} color={colorMap[status] || "default"} variant="contained" sx={{ fontSize: "0.7rem", height: "24px", borderRadius: "12px", fontWeight: "bold" }} />;
  };



  // Dynamic metric cards
  const metrics = [{ title: "Total Users", value: adminUser?.users?.response?.filter((user) => user.role === "CUSTOMER")?.length || 0, change: "59.3%", icon: <People />, description: "You made an extra 35,000 this year" }, { title: "Total Seller", value: adminUser?.users?.response?.filter((user) => user.role === "VENDOR")?.length || 0, change: "70.5%", icon: <People />, description: "You made an extra 8,900 this year" }, { title: "Total Employee", value: adminUser?.users?.response?.filter((user) => user.role === "EMPLOYEE")?.length || 0, change: "70.5%", icon: <People />, description: "You made an extra 8,900 this year" }, { title: "Total Products", value: adminProduct?.products?.response?.length || 0, change: "59.3%", icon: <Inventory />, description: "You made an extra 35,000 this year" }, { title: "Total Orders", value: adminOrder?.orders?.response?.length || 0, change: "27.4%", icon: <ShoppingCart />, description: "You made an extra 1,943 this year" }, {
    title: "Total Sales", value: "₹" + (
      adminOrder?.orders?.response
        ?.filter(order => order?.orderStatus === "DELIVERED")
        ?.reduce(
          (total, order) => total + (Number(order?.orderTotalPrice) || 0),
          0
        )
        .toFixed(2) || "0.00"
    )
    , change: "27.4%", icon: <AttachMoney />, description: "You made an extra $20,395 this year"
  }];


  const rows =
    adminOrder.recentOrders?.response?.filter((order) => order.orderStatus === "PENDING")?.map((order, index) => ({
      id: `${order.id}-${order?.orderItems?.map((item) => item?.product?.id).join("-")}-${index}`, // Unique ID for DataGrid
      orderId: order.orderId,
      orderDate: order.orderDate || "----",
      userDetail: `${order.shippingAddress?.name || order.user?.name} (${order.user?.email ? `${order.user?.email}, ${order.shippingAddress?.mobile || order.user?.phone}` : order.shippingAddress?.mobile || order.user?.phone})` || "----",
      shippingAddress: `${order.shippingAddress?.addressLine1}, ${order.shippingAddress?.postOffice}, ${order.shippingAddress?.city}, ${order.shippingAddress?.state}, ${order.shippingAddress?.country}, ${order.shippingAddress?.zipcode}, ${order.shippingAddress?.mobile}`,
      vendorName: order?.orderItems?.map((item) => item?.product?.vendor?.name).join(", ") || "----",
      vendorEmail: order?.orderItems?.map((item) => item?.product?.vendor?.email).join(", ") || "----",
      vendorPhone: order?.orderItems?.map((item) => item?.product?.vendor?.phone).join(", ") || "----",
      productImages: order?.orderItems
        ?.flatMap((item) =>
          (item?.product?.images || [])
            .filter((img) => img.isPrimary)
            .map((img) => img.fullUrl)
        )
        ?.filter(Boolean)
        ?.join(",") || "----",
      productTitle: order?.orderItems?.map((item) => item?.product?.title).join(", ") || "----",
      productName: order?.orderItems?.map((item) => item?.product?.productCategory?.name).join(", ") || "----",
      quantity: order?.orderItems?.map((item) => item?.quantity).join(", ") || 0,
      price: order?.orderItems?.map((item) => item?.variant?.vendorPrice?.toFixed(2)).join(", ") || 0.00,
      adminPrice: order?.orderItems?.map((item) => item?.variant?.payableAmount?.toFixed(2)).join(", ") || 0.00,
      size: order?.orderItems?.map((item) => item?.size).join(", ") || "----",
      color: order?.orderItems?.map((item) => item?.color).join(", ") || "----",
      totalQuantity: order.totalItem || 0,
      totalPrice: order?.orderItems?.reduce(
        (sum, item) => sum + ((item?.variant?.vendorPrice || 0.00) * (item?.quantity || 0)),
        0
      ).toFixed(2),
      totalAdminPrice: order.orderTotalPrice?.toFixed(2) || 0.00,
      orderStatus: order.orderStatus
    })
    ) || [];

  // Define columns
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 60,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "orderId", headerName: "Order ID", width: 120 },
    { field: "orderDate", headerName: "Order Date", width: 110, renderCell: (params) => dayjs(params?.value).format("DD MMM YYYY HH:mm"), },
    {
      field: "orderStatus",
      headerName: "Order Status",
      width: 100,
      renderCell: (params) => getStatusChip(params.value)

    },
    { field: "userDetail", headerName: "Customer Detail", width: 200 },
    { field: "shippingAddress", headerName: "Shipping Address", width: 250 },
    { field: "vendorName", headerName: "Vendor Name", width: 120 },
    { field: "vendorEmail", headerName: "Vendor Email", width: 150 },
    { field: "vendorPhone", headerName: "Vendor Phone", width: 120 },
    {
      field: "productImages",
      headerName: "Image(s)",
      width: 100,
      renderCell: (params) => {
        const images = params?.value
          ? params.value.split(",").map((url) => url.trim()) // multiple comma separated
          : [];

        if (images.length === 0) {
          return <span>----</span>;
        }

        return (
          <div style={{ display: "flex", gap: "5px" }}>
            {images.map((url, index) => (
              <img
                key={index}
                onClick={() => {
                  setImageOpen(true);
                  setImageUrl(url || defaultImage);
                }}
                src={url || defaultImage}
                alt={`product_image_${index}`}
                width={30}
                height={30}
                style={{ borderRadius: 8, objectFit: "cover", cursor: "pointer" }}
              />
            ))}
          </div>
        );
      },
    },
    { field: "productTitle", headerName: "Product Title", width: 150 },
    { field: "productName", headerName: "Product Name", width: 120 },
    { field: "quantity", headerName: "Quantity", width: 100 },
    { field: "price", headerName: "Vendor Price", width: 100 },
    { field: "adminPrice", headerName: "Customer Price", width: 100 },
    { field: "size", headerName: "Size", width: 100 },
    { field: "color", headerName: "Color", width: 100 },
    { field: "totalQuantity", headerName: "Total Quantity", width: 100 },
    { field: "totalPrice", headerName: "Total Vendor Price", width: 100 },
    { field: "totalAdminPrice", headerName: "Total Customer Price", width: 100 },
  ];

  // Chart data changes with tab (month/week)
  const analyticsData = {
    labels: adminUser?.visitor?.labels,
    datasets: [
      {
        label: "Customer Registration",
        data: adminUser?.visitor?.users,
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
      {
        label: "Vendor Registration",
        data: adminUser?.visitor?.vendors,
        borderColor: "#f87171",
        backgroundColor: "#f87171",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#f87171",
        pointBorderColor: "#f87171",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#f87171"
      },
    ],
  };

  const analyticsVendorData = {
    labels: ["Total Vendor",
      "Approved ",
      "Pending Approval",
      "Active  ",
      "Blocked "],
    datasets: [
      {
        label: "Vendor",
        data: [
          adminVendor?.vendors?.response?.length || 0,
          adminVendor?.vendors?.response?.filter(v => v.approved)?.length || 0,
          adminVendor?.vendors?.response?.filter(v => !v.approved)?.length || 0,
          adminVendor?.vendors?.response?.filter(v => !v.isBlocked)?.length || 0,
          adminVendor?.vendors?.response?.filter(v => v.isBlocked)?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  const analyticsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      labels: { font: { size: 12 }, },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { title: { display: true, text: tabValue === "year" ? "Month" : "Day" } },
      y: {
        beginAtZero: true, ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const analyticsProductData = {
    labels: ["Total Products",
      "Approved",
      "Rejected",
      "Pending",],
    datasets: [
      {
        label: "Products",
        data: [
          adminProduct?.products?.response?.length || 0,
          adminProduct?.products?.response?.filter(p => p.productStatus === "APPROVED")?.length || 0,
          adminProduct?.products?.response?.filter(p => p.productStatus === "REJECTED")?.length || 0,
          adminProduct?.products?.response?.filter(p => p.productStatus === "PENDING")?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  const analyticsOrderData = {
    labels: ["Total Orders",
      "Pending ",
      "Placed ",
      "Shipped ",
      "Delivered ",
      "Cancelled ",],
    datasets: [
      {
        label: "Orders Status",
        data: [
          adminOrder?.orders?.response?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.orderStatus === "PENDING")?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.orderStatus === "PLACED")?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.orderStatus === "SHIPPED")?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.orderStatus === "DELIVERED")?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.orderStatus === "CANCELLED")?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  const analyticsEmployeeOrderData = {
    labels: ["Orders Delivered", "On Time", "Before Time", "Beyond Time", "Issues/Cancelled"],
    datasets: [
      {
        label: "Orders Delivered",
        data: [
          adminOrder?.orderStats?.accepted || 0,
          adminOrder?.orderStats?.deliveredOnTime || 0,
          adminOrder?.orderStats?.deliveredBeforeTime || 0,
          adminOrder?.orderStats?.deliveredBeyondTime || 0,
          adminOrder?.orderStats?.issuesOrCancelled || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };
  const analyticsEmployeeAcceptOrderData = {
    labels: ["Total Orders", "Employee Accept", "Pending"],
    datasets: [
      {
        label: "Orders",
        data: [
          adminOrder?.orders?.response?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.employeeAcceptDate !== null && o.employee !== null)?.length || 0,
          adminOrder?.orders?.response?.filter(o => o.employeeAcceptDate === null && o.employee === null && o.orderStatus === "PENDING")?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2, minHeight: "100vh", bgcolor: "#f9f9f9" }}>
      <Grid container spacing={1}>
        {/* Metrics Cards */}
        {metrics.map((metric, index) =>
          <Grid item xs={12} sm={4} md={4} lg={2} key={index}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                borderRadius: 3,
                transition: "0.3s",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: 6
                }
              }}
            >
              <CardContent >
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {metric.title}
                </Typography>

                <Box display="flex" alignItems="center" mt={0.5}>
                  {/* Icon */}
                  <Box
                    sx={{
                      bgcolor: "#449AEF",
                      color: "white",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {metric.icon}
                  </Box>

                  {/* Value */}
                  {/* <Typography variant="h6" marginLeft={0.5}>
                    {metric.value}
                  </Typography> */}
                  <Typography
                    variant="h6"
                    marginLeft={0.5}
                    sx={{
                      fontSize: "clamp(0.5rem, 1.5vw, 1rem)", // auto adjust
                      maxWidth: "170px", // fix width
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Vendor</Typography>
          </Box>
          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsVendorData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>


        {/* Income */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" mb={2}>
            Income
          </Typography>
          <Card
            sx={{
              height: { xs: "auto", md: 350 },
              p: { xs: 1.5, md: 2 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <CardContent>
              {[
                { label: "Today", value: adminOrder?.income?.today },
                { label: "This Week", value: adminOrder?.income?.week },
                { label: "This Month", value: adminOrder?.income?.month },
                { label: "This Year", value: adminOrder?.income?.year },
              ].map(({ label, value }, index) => (
                <Box key={index} sx={{ mb: index !== 3 ? 2 : 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {label} Statistics
                  </Typography>
                  <Typography variant="h6" sx={{ wordWrap: "break-word" }}>
                    ₹{value?.toFixed(2) || 0.00}
                  </Typography>
                  {index !== 3 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Registrations</Typography>
            <CustomTabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
              <CustomTab value="week" label="Week" />
              <CustomTab value="month" label="Month" />
              <CustomTab value="year" label="Year" />
            </CustomTabs>
          </Box>
          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsData} options={options} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Products</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsProductData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Internal Orders Status</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsEmployeeAcceptOrderData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Orders</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsOrderData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Delivery Statistics</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsEmployeeOrderData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Orders */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" mb={2}>
            Recent Orders
          </Typography>
          <Card>
            <CardContent>
              <div style={{ height: "100%", width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pagination
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                  }}
                  pageSizeOptions={[10, 25, 50, 75, 100]}
                  disableSelectionOnClick
                  density="compact"
                  experimentalFeatures={{ virtualization: true }}
                  loading={adminOrder.loading}
                  autoHeight={true}
                  sx={{
                    borderRadius: 2,
                    fontSize: "0.8rem",
                    "& .MuiDataGrid-cell": {
                      fontSize: "0.8rem",
                      border: "1px solid rgb(204, 201, 201)",
                      minHeight: "36px !important",
                      height: "36px !important",
                      lineHeight: "1.2",
                      paddingTop: "4px",
                      paddingBottom: "4px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      backgroundColor: "#449AEF",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "0.8rem",
                      padding: "8px",
                      border: "1px solid rgb(204, 201, 201)",
                      minHeight: "36px !important",
                      height: "36px !important",
                      lineHeight: "1.2",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    },
                    "& .MuiDataGrid-row": {
                      maxHeight: "36px !important",
                      minHeight: "36px !important",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Transaction History */}

        <Grid item xs={12} md={4}>
          <Typography variant="h6" mb={2}>
            {/* Delivered Status */}
            Recent Delivered Orders
          </Typography>
          <Card sx={{ height: 400, p: { xs: 1.5, md: 2 } }}>
            <CardContent>
              <List disablePadding>
                {adminOrder?.orders?.response?.filter(order => order?.orderStatus === "DELIVERED")?.length > 0 ? (
                  adminOrder?.orders?.response
                    ?.filter(order => order?.orderStatus === "DELIVERED")
                    ?.slice(0, 4)
                    ?.map((order, index) => (
                      <React.Fragment key={order?.id || index}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 1,
                          }}
                        >
                          <ListItemText
                            primary={`Order #${order?.orderId?.slice(0, 5)}...`}
                            secondary={
                              order?.createdAt
                                ? dayjs(order.createdAt).format("DD MMM YYYY HH:mm")
                                : "----"
                            }
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: "success.main", minWidth: "70px", textAlign: "right" }}
                          >
                            ₹{order?.orderTotalPrice?.toFixed(2) || 0.00}
                          </Typography>
                        </ListItem>
                        {index < 3 && <Divider sx={{ my: 1 }} />}
                      </React.Fragment>
                    ))) : (
                  <ListItem>
                    <ListItemText primary="No transactions available" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Common Popup */}
      <ImagePopup
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        imageUrl={imageUrl}
      />
    </Box>
  );
};

export default Dashboard;